import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { TranslatePipe } from '@cmi/addin-core';
import { Button, LoadingSpinner, Notification } from '@cmi/ui';
import * as wordControls from '../../../../lib/word-content-controls';
import { FeatureToggleService } from '../../../shared/feature-toggle.service';
import { SelectorProviderService } from '../../../shared/selector-provider.service';

@Component({
  selector: 'cmi-reports-format',
  imports: [TranslatePipe, Notification, Button, LoadingSpinner],
  templateUrl: './reports-format.html'
})
export class ReportsFormat {
  private readonly _selectorProviderService = inject(SelectorProviderService);
  private readonly _featureToggleService = inject(FeatureToggleService);

  public readonly contentControlId = input.required<number>();

  protected readonly tag = signal<string>('');
  protected readonly title = computed(() => {
    const tag = this.tag();
    const pathPart = tag.startsWith('path:') ? tag.slice('path:'.length) : tag;
    const path = pathPart.split(';')[0];
    return path.split('\\').filter(Boolean).at(-1) ?? path;
  });
  protected readonly isExpanded = signal<boolean>(false);
  protected readonly isSaving = signal<boolean>(false);
  protected readonly duplicateError = signal<boolean>(false);
  protected readonly saveError = signal<boolean>(false);
  protected readonly selectedProvider = signal<string>('');

  protected readonly selectorProvidersSupported = computed(() =>
    this._featureToggleService.supportsSelectorProviders()
  );
  protected readonly selectorProviders = computed(() => this._selectorProviderService.providers());
  protected readonly selectorProvidersLoading = computed(() =>
    this._selectorProviderService.isLoading()
  );
  protected readonly selectorProvidersError = computed(() => this._selectorProviderService.error());

  public constructor() {
    effect(() => {
      const id = this.contentControlId();
      if (id) {
        void this._loadContentControl();
      }
    });
  }

  private async _loadContentControl(): Promise<void> {
    try {
      const controls = await wordControls.getAllContentControls();
      const control = controls.find((c) => c.id === this.contentControlId());
      if (control) {
        this.tag.set(control.tag ?? '');
        const parsedTag = wordControls.parseContentControlTag(control.tag ?? '');
        if (parsedTag.selector) {
          const providerProperty = await wordControls.getProperty(
            `Selector_${parsedTag.selector}.Key`
          );
          this.selectedProvider.set(providerProperty?.value ?? '');
        } else {
          this.selectedProvider.set('');
        }
      }
    } catch {
      // ignore
    }
  }

  protected toggleExpanded(): void {
    this.isExpanded.update((v) => !v);
  }

  protected onTagInput(event: Event): void {
    this.duplicateError.set(false);
    this.saveError.set(false);
    this.tag.set((event.target as HTMLInputElement).value);
  }

  protected onProviderChange(event: Event): void {
    this.selectedProvider.set((event.target as HTMLSelectElement).value);
  }

  protected reloadSelectorProviders(): void {
    this._selectorProviderService.reload();
  }

  protected async onSave(): Promise<void> {
    this.isSaving.set(true);
    this.duplicateError.set(false);
    this.saveError.set(false);
    try {
      const existing = await wordControls.getAllContentControls();
      const id = this.contentControlId();
      const title = this.title();
      const providerKey = this.selectedProvider();
      const currentTag = wordControls.parseContentControlTag(this.tag());
      const previousTag = wordControls.parseContentControlTag(
        existing.find((control) => control.id === id)?.tag ?? ''
      );

      const selectorKey = providerKey || undefined;
      const nextTag = wordControls.serializeContentControlTag({
        ...currentTag,
        selector: selectorKey
      });

      const prefixedTitle = `CMR_${title}`;
      if (existing.some((control) => control.title === prefixedTitle && control.id !== id)) {
        this.duplicateError.set(true);
        return;
      }

      await wordControls.updateContentControl(id, prefixedTitle, nextTag, prefixedTitle);

      if (previousTag.selector && previousTag.selector !== selectorKey) {
        await wordControls.removeReportSelectorPropertiesIfUnused(previousTag.selector, id);
      }

      if (providerKey && selectorKey) {
        await wordControls.ensureReportSelectorProperties(selectorKey, providerKey);
      }

      this.selectedProvider.set(providerKey);
      this.tag.set(nextTag);
    } catch {
      this.saveError.set(true);
    } finally {
      this.isSaving.set(false);
    }
  }
}
